import { PrijelaznoRazdoblje } from "@/components/PrijelaznoRazdoblje";
import { usePostavke } from "@/lib/postavke";
import { dohvatiPregledPopisa, type StatusPodobnosti } from "@/lib/siteDossier";
import { useEffect, useState } from "react";
import "./RosterAssignmentDialog.css";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, Select } from "@/components/ui/design-system";
import { PeriodLockNotice, usePeriodLock } from "@/components/workflow/shared";
import { useAuth } from "@/context/AuthContext";
import { danas } from "@/lib/dan";
import { datum } from "@/lib/format";
import {
  dohvatiRaspored,
  rasporediRadnikeBatch,
  type IshodBatchRasporeda,
  type Raspored,
} from "@/lib/gradilista";
import { porukaDohvata } from "@/lib/greske";
import { dohvatiRadnike, punoIme, type Radnik } from "@/lib/radnici";
import { workflow, type Crew } from "@/lib/workflow";
import { rosterControl, rosterPanel } from "@/pages/GradilisniPregledPage";

export function shiftDay(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

async function allWorkers(signal: AbortSignal): Promise<Radnik[]> {
  const workers: Radnik[] = [];
  let total = Infinity;
  while (workers.length < total) {
    const page = await dohvatiRadnike(
      { limit: 200, offset: workers.length, samo_aktivni: true },
      signal,
    );
    workers.push(...page.items);
    total = page.total;
    if (page.items.length === 0) break;
  }
  return workers;
}

export function RosterAssignmentDialog({
  siteId,
  siteName,
  initialWorker,
  onClose,
  onSaved,
}: {
  siteId: string;
  siteName: string;
  initialWorker?: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { can } = useAuth();
  const { prijelazno } = usePostavke();
  const [start, setStart] = useState(() => shiftDay(danas(), 1));
  const [search, setSearch] = useState("");
  const [firstUnassigned, setFirstUnassigned] = useState(true);
  const [transfer, setTransfer] = useState(() => can("raspored", "update"));
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialWorker ? [initialWorker] : []),
  );
  const [crew, setCrew] = useState("");
  const [crews, setCrews] = useState<Crew[]>([]);
  const [workers, setWorkers] = useState<Radnik[]>([]);
  const [statuses, setStatuses] = useState<
    { worker_id: string; status: StatusPodobnosti; reasons: { obligation: string }[] }[]
  >([]);
  const [rosters, setRosters] = useState<Raspored[]>([]);
  const [loadedDate, setLoadedDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState<IshodBatchRasporeda[] | null>(null);
  const lock = usePeriodLock(siteId, start, "9999-12-31");
  const readCrews = can("brigada", "read");
  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      allWorkers(controller.signal),
      dohvatiPregledPopisa(danas(), controller.signal),
      dohvatiRaspored(undefined, start, controller.signal),
      readCrews
        ? workflow.crews(siteId, controller.signal)
        : Promise.resolve([]),
    ])
      .then(([people, eligibility, assignments, teams]) => {
        if (controller.signal.aborted) return;
        setWorkers(people);
        setStatuses([
          ...eligibility.gradilista.flatMap((site) =>
            site.radnici.map((worker) => ({
              worker_id: worker.radnik_id,
              status: worker.eligibility,
              reasons: worker.reasons ?? [],
            })),
          ),
          ...eligibility.bez_gradilista.map((worker) => ({
            worker_id: worker.radnik_id,
            status: worker.eligibility,
              reasons: worker.reasons ?? [],
          })),
        ]);
        setRosters(assignments);
        setCrews(teams.filter((team) => team.aktivna));
        setLoadedDate(start);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(porukaDohvata(e));
      });
    return () => controller.abort();
  }, [readCrews, siteId, start]);
  const current = (worker: string) =>
    rosters.filter(
      (item) => item.radnik_id === worker && (prijelazno || !item.ceka_blokada_override),
    );
  const candidates = workers
    .filter(
      (worker): worker is Radnik & { id: string } =>
        Boolean(worker.id) &&
        !rosters.some(
          (item) =>
            item.radnik_id === worker.id && item.gradiliste_id === siteId,
        ) &&
        punoIme(worker)
          .toLocaleLowerCase("hr")
          .includes(search.trim().toLocaleLowerCase("hr")),
    )
    .sort(
      (a, b) =>
        (firstUnassigned
          ? Number(current(a.id).length > 0) - Number(current(b.id).length > 0)
          : 0) || punoIme(a).localeCompare(punoIme(b), "hr"),
    );
  const ready = loadedDate === start;
  async function save() {
    if (!start || !ready || saving || lock.blocked || selected.size === 0)
      return;
    setSaving(true);
    setError(null);
    try {
      const available = new Set(
        workers
          .filter(
            (worker) =>
              !rosters.some(
                (item) =>
                  item.radnik_id === worker.id && item.gradiliste_id === siteId,
              ),
          )
          .map((worker) => worker.id),
      );
      const ids = [...selected].filter((id) => available.has(id));
      if (ids.length === 0) {
        setError("Odaberite dostupne radnike.");
        return;
      }
      const outcomes = await rasporediRadnikeBatch({
        radnik_ids: ids,
        gradiliste_id: siteId,
        vrijedi_od: start,
        brigada_id: crew || null,
        premjesti: transfer,
      });
      setResults(outcomes);
      await onSaved();
    } catch (e: unknown) {
      setError(porukaDohvata(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog
      title={`Dodaj radnike — ${siteName}`}
      className="roster-assignment-dialog"
      onClose={() => {
        if (!saving) onClose();
      }}
      actions={
        results ? (
          <Button className="min-h-11" onClick={onClose}>
            Zatvori rezultate
          </Button>
        ) : (
          <>
            <Button
              className="min-h-11"
              variant="outline"
              disabled={saving}
              onClick={onClose}
            >
              Odustani
            </Button>
            <Button
              className="min-h-11"
              disabled={saving || !ready || lock.blocked || !selected.size}
              onClick={() => void save()}
            >
              {saving ? "Spremanje…" : `Dodaj ${selected.size} radnika`}
            </Button>
          </>
        )
      }
    >
      <PrijelaznoRazdoblje />
      {error && (
        <p role="alert" className="mb-3 text-red-800">
          {error}
        </p>
      )}
      {results ? (
        <>
          <h3 className="font-semibold">Rezultat po radniku</h3>
          <ul className="my-4 divide-y">
            {results.map((result) => (
              <li key={result.radnik_id} className="py-3">
                <strong>
                  {punoIme(
                    workers.find((worker) => worker.id === result.radnik_id) ??
                      {},
                  )}
                </strong>
                :{" "}
                {result.status === "uspjeh"
                  ? "Dodan"
                  : result.status === "ceka_override"
                    ? "Čeka odobrenje iznimke"
                    : "Greška"}
                {result.razlog && (
                  <p className="mt-1 text-sm">{result.razlog}</p>
                )}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <label>
              Pretraga radnika
              <input
                className={rosterControl}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <label>
              Od
              <input
                className={rosterControl}
                type="date"
                value={start}
                onChange={(e) => {
                  if (e.target.value) {
                    setStart(e.target.value);
                    setSelected(new Set());
                  }
                }}
              />
            </label>
            <label>
              Brigada (neobavezno)
              <Select
                className={rosterControl}
                value={crew}
                onChange={(e) => setCrew(e.target.value)}
              >
                <option value="">Bez brigade</option>
                {crews.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.naziv}
                  </option>
                ))}
              </Select>
            </label>
          </div>
          <label className="my-2 flex min-h-11 items-center gap-3">
            <input
              type="checkbox"
              className="size-5"
              checked={firstUnassigned}
              onChange={(e) => setFirstUnassigned(e.target.checked)}
            />
            Prvo oni koji nisu nigdje raspoređeni
          </label>
          <label className="my-2 flex min-h-11 items-center gap-3">
            <input
              type="checkbox"
              className="size-5"
              checked={transfer}
              disabled={!can("raspored", "update")}
              onChange={(e) => setTransfer(e.target.checked)}
            />
            Premjesti: na drugim gradilištima završi dan prije
          </label>
          <p className="my-2 text-sm">
            Podobnost je prikazana za danas. Pri spremanju provjerava se
            odabrani početni dan. {prijelazno ? "Razlozi se prikazuju kao upozorenja." : "Nepodobni čekaju odobrenje iznimke."}
          </p>
          <PeriodLockNotice state={lock.state} />
          {!ready && !error ? (
            <p role="status">Učitavanje radnika i rasporeda…</p>
          ) : (
            <div className={`${rosterPanel} my-3 max-h-[45vh] overflow-auto`}>
              <div className="hidden grid-cols-[2fr_2fr_1fr] gap-3 border-b p-3 text-sm font-semibold sm:grid">
                <span>Radnik</span>
                <span>Sada je na</span>
                <span>Podobnost</span>
              </div>
              {candidates.map((worker) => {
                const assignments = current(worker.id).filter(
                  (item) => item.gradiliste_id !== siteId,
                );
                const status = statuses.find(
                  (item) => item.worker_id === worker.id,
                );
                return (
                  <label
                    key={worker.id}
                    className="grid min-h-11 gap-2 border-b p-3 last:border-b-0 sm:grid-cols-[2fr_2fr_1fr]"
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="size-5 shrink-0"
                        checked={selected.has(worker.id)}
                        onChange={(e) =>
                          setSelected((old) => {
                            const next = new Set(old);
                            if (e.target.checked) next.add(worker.id);
                            else next.delete(worker.id);
                            return next;
                          })
                        }
                      />
                      <span>
                        <strong>{punoIme(worker)}</strong>
                        <span className="block text-sm">
                          {worker.radno_mjesto || "—"}
                        </span>
                      </span>
                    </span>
                    <span className="text-sm">
                      {assignments.length
                        ? assignments.map((item) => (
                            <span className="block" key={item.id}>
                              {transfer
                                ? `Premješta se: na ${item.gradiliste || "drugom gradilištu"} završava ${datum(shiftDay(start, -1))}`
                                : `Sada je na: ${item.gradiliste || "drugom gradilištu"} (ostaje)`}
                            </span>
                          ))
                        : "Nije nigdje raspoređen"}
                    </span>
                    <span>
                      <Badge
                        ton={
                          !status
                            ? "idle"
                            : status.status === "podoban"
                              ? "ok"
                              : "bad"
                        }
                      >
                        {!status
                          ? "Provjera pri spremanju"
                          : status.status === "podoban"
                            ? "Smije raditi"
                            : prijelazno ? "Ne bi smio raditi · prijelazno" : "Čeka iznimku"}
                      </Badge>
                      {status?.reasons.map((reason) => <p className="mt-1 text-sm text-red-800" key={reason.obligation}>{reason.obligation}</p>)}
                    </span>
                  </label>
                );
              })}
              {candidates.length === 0 && (
                <p className="p-4">Nema dostupnih radnika za ovaj odabir.</p>
              )}
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}
